import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { IconButton } from './Button';
import s from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Header content after the title (navigation, pills, the simulation line). */
  headerExtra?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** For full-bleed bodies (charts) that manage their own padding. */
  flush?: boolean;
  /** Blocking dialogs (legal accept) cannot be dismissed. */
  dismissible?: boolean;
  labelledBy?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The one dialog. Esc and the scrim close it; focus moves into it on open, stays inside it (Tab
 * wraps at either end) and returns to the previously focused element on close. Named by its
 * title, or by `labelledBy` for a dialog whose heading lives in its body.
 */
export function Modal({
  open,
  onClose,
  title,
  headerExtra,
  children,
  footer,
  size = 'md',
  flush,
  dismissible = true,
  labelledBy,
}: ModalProps) {
  const dialog = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // **The latest `onClose`, read through a ref.** Callers pass it inline, so it is a new function on
  // every render; as an effect dependency it re-ran the effect on each keystroke in a field inside
  // the dialog, and the cleanup handed focus back to the page behind it after every character.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) closeRef.current();
      if (e.key !== 'Tab' || !dialog.current) return;
      const focusable = Array.from(dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const current = document.activeElement;
      if (e.shiftKey && (current === first || current === dialog.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, dismissible]);

  if (!open) return null;
  return createPortal(
    <>
      <div className={s.scrim} onClick={dismissible ? onClose : undefined} />
      <div ref={dialog} role="dialog" aria-modal aria-labelledby={labelledBy ?? (title ? titleId : undefined)} tabIndex={-1} className={cx(s.dialog, s[size])}>
        {(title || headerExtra || dismissible) && (
          <header className={s.header}>
            {title && (
              <h2 id={titleId} className={s.title}>
                {title}
              </h2>
            )}
            {headerExtra}
            {dismissible && <IconButton icon="close" label="Close (Esc)" size="sm" onClick={onClose} className={s.close} />}
          </header>
        )}
        <div className={cx(s.body, flush && s.flush)}>{children}</div>
        {footer && <footer className={s.footer}>{footer}</footer>}
      </div>
    </>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  danger,
  busy,
  confirmDisabled,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /** Holds the confirm button disabled until the caller's own condition is met. */
  confirmDisabled?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button className={s.textButton} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            type="button"
            className={cx(s.confirm, danger && s.confirmDanger)}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className={s.confirmBody}>{children}</div>
    </Modal>
  );
}
